import { BehaviorSubject } from 'rxjs';

import { VzTag } from '@models/tags/tag';
import { KanbanBoard } from '@models/tasks/kanban-board';
import { UserNotification } from '@models/events/user-notification';
import { Group } from '@models/users/group';
import { Org } from '@models/users/org';
import { Person } from '@models/users/person';
import { Project } from '@models/projects/project';
import { Role } from '@models/users/role';

import { GroupsState } from './groups-state';
import { OrgsState } from './orgs-state';
import { PersonsState } from './persons-state';
import { ProjectsState } from './projects-state';
import { RolesState } from './roles-state';
import { TagsState } from './tags-state';
import { UserNotificationsState } from './user-notifications-state';
import { UrlState } from './url-state';
import { AppLogState } from './app-log-state';
import { UserState } from './user-state';
import { PrefsState } from './prefs-state';
import { StompLogState } from './stomp-log-state';
import { VideoState } from './video-state';
import { CountersState } from './counters-state';
import { FeedbackState } from './feedback-state';
import { IntegrationsState } from './integrations-state';
import { IntegrationSetup } from '../integrations';

export const StateCacheKeys = ['orgs', 'tags', 'roles', 'groups', 'persons', 'projects', 'notifications', 'integrations'] as const;
export const StateKeys = [...StateCacheKeys, 'url', 'user', 'prefs', 'stomp', 'counters', 'video', 'feedback'] as const;
export const DeletedStatesKeys = ['boards', 'chats', 'orgstatustypes', 'status'] as const;

export type StateCacheKeyType = typeof StateCacheKeys[number];
export type StateCacheType = {
    orgs: OrgsState,
    tags: TagsState,
    roles: RolesState,
    groups: GroupsState,
    persons: PersonsState,
    projects: ProjectsState,
    notifications: UserNotificationsState,
    integrations: IntegrationsState,
};
export type StateCacheItemType = {
    orgs: Org,
    tags: VzTag,
    roles: Role,
    boards: KanbanBoard,
    groups: Group,
    persons: Person,
    projects: Project,
    integrations: IntegrationSetup,
    notifications: UserNotification,
}
export type StateCacheMapsType = { [T in StateCacheKeyType]?: { [id: string]: StateCacheItemType[T] } };
export type StateCacheArrayType = { [T in StateCacheKeyType]?: StateCacheItemType[T][] };

export type StateKeyType = typeof StateKeys[number];
export type StatesType = StateCacheType & {
    url: UrlState,
    log: AppLogState,
    user: UserState,
    prefs: PrefsState,
    stomp: StompLogState,
    video: VideoState,
    counters: CountersState,
    feedback: FeedbackState,
};
export type StatesMapType = { [T in StateKeyType]: BehaviorSubject<StatesType[T]> };

export const newStateInstance: { [T in StateKeyType]: (v: any) => StatesType[T] } = {
    url: v => new UrlState(v),
    user: v => new UserState(v),
    orgs: v => new OrgsState(v),
    tags: v => new TagsState(v),
    roles: v => new RolesState(v),
    prefs: v => new PrefsState(v),
    stomp: v => new StompLogState(v),
    video: v => new VideoState(v),
    groups: v => new GroupsState(v),
    persons: v => new PersonsState(v),
    projects: v => new ProjectsState(v),
    counters: v => new CountersState(v),
    feedback: v => new FeedbackState(v),
    integrations: v => new IntegrationsState(v),
    notifications: v => new UserNotificationsState(v),
};

export type StateFilterType = { [T in StateCacheKeyType]?: (item: StateCacheItemType[T]) => boolean };
export function stateOrgFilter(oid: string): StateFilterType {
    return {
        orgs: item => item.id == oid,
        tags: item => item.orgId == oid,
        roles: item => item.orgId == oid,
        groups: item => item.orgId == oid,
        persons: item => !!item._orgMap[oid],
        projects: item => item.orgId == oid,
        integrations: item => item.orgId == oid,
        notifications: () => true,
    };
}
export function stateOrgFilterFn(oidFn: () => string): StateFilterType {
    return {
        orgs: item => item.id == oidFn(),
        tags: item => item.orgId == oidFn(),
        roles: item => item.orgId == oidFn(),
        groups: item => item.orgId == oidFn(),
        persons: item => !!item._orgMap[oidFn()],
        projects: item => item.orgId == oidFn(),
        integrations: item => item.orgId == oidFn(),
        notifications: () => true,
    };
}

export type FilterType<T extends StateKeyType, K> = (state: StatesType[T]) => K;
